import { useQuery } from '@tanstack/react-query'
import { queryClient } from '@/lib/react-query'
import { useAuthStore } from '@/store/useAuthStore'
import { useApiReady } from '@/hooks/useApiReady'
import { getFreelancerTeams } from '@/apis/freelancers'

export const userTeamsKeys = {
  all: ['userTeams'] as const,
  list: (userId: string | undefined) => [...userTeamsKeys.all, userId] as const,
}

export interface UserTeamSummary {
  id: string
  name: string
  company_type?: string
}

/** Teams seedées par GET /auth/session (freelancers). */
export function getSeededUserTeams(userId: string | undefined): UserTeamSummary[] | undefined {
  if (!userId) return undefined
  return queryClient.getQueryData<UserTeamSummary[]>(userTeamsKeys.list(userId))
}

/**
 * Équipes du freelancer — seed session, fallback GET /freelancers/teams si cache vide.
 */
export function useUserTeams() {
  const { user, globalRole } = useAuthStore()
  const apiReady = useApiReady()
  const isFreelancer = globalRole === 'freelancer' || user?.role === 'freelancer'
  const seeded = getSeededUserTeams(user?.id)
  const seededUpdatedAt = seeded
    ? queryClient.getQueryState(userTeamsKeys.list(user?.id))?.dataUpdatedAt ?? Date.now()
    : undefined

  return useQuery({
    queryKey: userTeamsKeys.list(user?.id),
    queryFn: async () => {
      if (globalRole === 'super-admin') {
        return []
      }
      if (!isFreelancer) {
        return []
      }

      try {
        const res = await getFreelancerTeams()
        if (res?.data?.code === 200) {
          return res.data.data?.teams || []
        }
        if (res?.code === 200) {
          return res.data?.teams || []
        }
        return []
      } catch (error: unknown) {
        const err = error as { response?: { status?: number } }
        if (err?.response?.status === 403 || err?.response?.status === 404) {
          return []
        }
        throw error
      }
    },
    enabled: apiReady && globalRole !== 'super-admin' && isFreelancer && !seeded,
    initialData: seeded,
    initialDataUpdatedAt: seededUpdatedAt,
    refetchOnMount: seeded ? false : true,
    staleTime: 1000 * 60 * 10,
    retry: 1,
  })
}
